import React, { useState, useEffect, useContext, memo } from "react";
import { useSnackbar } from "notistack";
import { AuthContext } from "../context/AuthContext";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from "@mui/material";
import { orange } from "@mui/material/colors";
import { Line, Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import {
  getKumesler,
  getMuhasebeDonemleri,
  getKarZararRaporu,
  getMaliyetRaporuGrafik,
  getMaliyetRaporuListe,
} from "../services/api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const formatDate = (item) => {
  const dateValue = item.TARIH_STR || item.TARIH;
  if (typeof dateValue === "string" && dateValue.length > 0) return dateValue;
  if (
    typeof dateValue === "object" &&
    dateValue !== null &&
    dateValue.year &&
    dateValue.month &&
    dateValue.day
  ) {
    const day = String(dateValue.day).padStart(2, "0");
    const month = String(dateValue.month).padStart(2, "0");
    return `${day}.${month}.${dateValue.year}`;
  }
  return "Tarih Yok";
};

const reportSectionStyle = {
  padding: { xs: 1, sm: 2 },
  borderRadius: 2,
  border: "2px solid transparent",
  transition: "border-color 0.3s ease-in-out",
  "&:hover": {
    borderColor: orange[500],
  },
};

const formControlStyle = {
  "& .MuiOutlinedInput-root": {
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: orange[400],
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: orange[600],
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: orange[800],
  },
};

const tableHeaderStyle = {
  "& .MuiTableCell-root": {
    backgroundColor: orange[800],
    color: "white",
    fontWeight: "bold",
  },
};

const MuhasebeRaporlari = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { token } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState(0);
  const [kumesler, setKumesler] = useState([]);
  const [donemler, setDonemler] = useState([]);
  const [selectedKumes, setSelectedKumes] = useState("");
  const [selectedDonem, setSelectedDonem] = useState("");
  const [selectedDoviz, setSelectedDoviz] = useState(1);
  const [reportData, setReportData] = useState([]);
  const [maliyetGrafikData, setMaliyetGrafikData] = useState([]);
  const [maliyetDetayData, setMaliyetDetayData] = useState([]);
  const [loading, setLoading] = useState(false); // false olarak değiştirildi
  const [loadingDonemler, setLoadingDonemler] = useState(false);

  // İlk yüklemede sadece kümes listesini çek
  useEffect(() => {
    if (!token) return;

    const fetchKumesler = async () => {
      try {
        const kumesRes = await getKumesler();
        setKumesler(kumesRes.data.data || []);
      } catch (err) {
        enqueueSnackbar("Kümes listesi yüklenemedi: " + err.message, {
          variant: "error",
        });
      }
    };

    fetchKumesler();
  }, [token, enqueueSnackbar]);

  // Kümes seçildiğinde dönem listesini getir
  useEffect(() => {
    if (selectedKumes) {
      setLoadingDonemler(true);
      setDonemler([]);
      setSelectedDonem("");
      getMuhasebeDonemleri(selectedKumes)
        .then((res) => setDonemler(res.data.data || []))
        .catch((err) =>
          enqueueSnackbar("Dönemler alınamadı", { variant: "error" })
        )
        .finally(() => setLoadingDonemler(false));
    } else {
      setDonemler([]);
      setSelectedDonem("");
    }
  }, [selectedKumes, enqueueSnackbar]);

  const handleListele = async () => {
    setLoading(true);
    setReportData([]);
    setMaliyetGrafikData([]);
    setMaliyetDetayData([]);

    const kumesId = selectedKumes || 0;
    const donemId = selectedDonem || 0;

    try {
      if (activeTab === 0) {
        const response = await getKarZararRaporu({
          kumesId,
          donemId,
          dovizId: selectedDoviz,
        });
        const data = response.data.data || [];
        setReportData(Array.isArray(data) ? data : [data]);
      } else {
        const [grafikRes, detayRes] = await Promise.all([
          getMaliyetRaporuGrafik({ kumesId, donemId }),
          getMaliyetRaporuListe({ kumesId, donemId }),
        ]);
        const grafikData = grafikRes.data.data || [];
        const detayData = detayRes.data.data || [];
        setMaliyetGrafikData(
          Array.isArray(grafikData) ? grafikData : [grafikData]
        );
        setMaliyetDetayData(Array.isArray(detayData) ? detayData : [detayData]);
      }
    } catch (err) {
      enqueueSnackbar(`Rapor verileri alınamadı: ${err.message}`, {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (num, currencyId = selectedDoviz) => {
    const currencyMap = { 1: "TRY", 2: "USD", 3: "EUR" };
    return Number(num || 0).toLocaleString("tr-TR", {
      style: "currency",
      currency: currencyMap[currencyId] || "TRY",
    });
  };

  const barChartData = {
    labels: reportData.map((item) => formatDate(item)),
    datasets: [
      {
        label: "Toplam Gelir",
        data: reportData.map((item) => item.TOPLAM_GELIR),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
      {
        label: "Toplam Gider",
        data: reportData.map((item) => item.TOPLAM_GIDER),
        backgroundColor: "rgba(255, 99, 132, 0.6)",
      },
      {
        label: "Net Kar",
        data: reportData.map((item) => item.NET_KAR),
        backgroundColor: "rgba(54, 162, 235, 0.8)",
      },
    ],
  };
  const lineChartData = {
    labels: reportData.map((item) => formatDate(item)),
    datasets: [
      {
        label: "Performans Puanı",
        data: reportData.map((item) => item.PERFORMANS_PUAN),
        borderColor: "rgb(255, 159, 64)",
        yAxisID: "y",
      },
      {
        label: "Net Kar",
        data: reportData.map((item) => item.NET_KAR),
        borderColor: "rgb(54, 162, 235)",
        yAxisID: "y1",
      },
    ],
  };
  const commonChartOptions = { responsive: true, maintainAspectRatio: false };
  const lineChartOptions = {
    ...commonChartOptions,
    plugins: {
      title: {
        display: true,
        text: "Performans / Net Kar Mukayese Grafiği",
        font: { size: 16 },
      },
    },
    scales: {
      y: {
        position: "left",
        title: { display: true, text: "Performans Puanı" },
      },
      y1: {
        position: "right",
        title: { display: true, text: "Net Kar" },
        grid: { drawOnChartArea: false },
      },
    },
  };
  const barChartOptions = {
    ...commonChartOptions,
    plugins: {
      title: { display: true, text: "Kâr Zarar Grafiği", font: { size: 16 } },
    },
  };
  const pieChartData = {
    labels: maliyetGrafikData.map((item) => item.MALIYET_TIP_ADI),
    datasets: [
      {
        label: "Maliyet (TL)",
        data: maliyetGrafikData.map((item) => item.MALIYET_TL),
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
          "#E7E9ED",
          "#8DDF3C",
          "#F17171",
          "#7F00FF",
          "#C9DE00",
          "#2F4F4F",
        ],
      },
    ],
  };
  const pieChartOptions = {
    ...commonChartOptions,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Maliyet Dağılımı Grafiği",
        font: { size: 16 },
      },
    },
  };

  return (
    <Paper
      sx={{
        p: { xs: 1, sm: 2 },
        mt: 2,
        overflowX: "hidden",
        backgroundColor: "#f9f9f9",
      }}
    >
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: "bold", color: orange[800] }}
      >
        Muhasebe Raporları
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        sx={{ mb: 3 }}
      >
        <Tab label="Kar/Zarar Raporları" />
        <Tab label="Maliyet Raporları" />
      </Tabs>

      <Paper sx={{ p: 2, mb: 4 }} elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <FormControl
              sx={{ minWidth: 200, ...formControlStyle }}
              size="small"
            >
              <InputLabel>Kümes</InputLabel>
              <Select
                value={selectedKumes}
                onChange={(e) => setSelectedKumes(e.target.value)}
                label="Kümes"
              >
                <MenuItem value="">
                  <em>Tümü</em>
                </MenuItem>
                {(kumesler || []).map((k) => (
                  <MenuItem key={k.KUMES_ID} value={k.KUMES_ID}>
                    {k.KUMES_ADI}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl
              sx={{ minWidth: 200, ...formControlStyle }}
              size="small"
              disabled={!selectedKumes || loadingDonemler}
            >
              <InputLabel>
                {loadingDonemler ? "Yükleniyor..." : "Dönem"}
              </InputLabel>
              <Select
                value={selectedDonem}
                onChange={(e) => setSelectedDonem(e.target.value)}
                label="Dönem"
              >
                <MenuItem value="">
                  <em>Tümü</em>
                </MenuItem>
                {(donemler || []).map((d) => (
                  <MenuItem key={d.DONEM_ID} value={d.DONEM_ID}>
                    {d.KUMES_DONEM_ADI}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {activeTab === 0 && (
            <Grid item>
              <FormControl
                sx={{ minWidth: 120, ...formControlStyle }}
                size="small"
              >
                <InputLabel>Döviz</InputLabel>
                <Select
                  value={selectedDoviz}
                  onChange={(e) => setSelectedDoviz(e.target.value)}
                  label="Döviz"
                >
                  <MenuItem value={1}>TL</MenuItem>
                  <MenuItem value={2}>USD</MenuItem>
                  <MenuItem value={3}>EUR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item>
            <Button
              variant="contained"
              onClick={handleListele}
              disabled={loading}
              sx={{
                backgroundColor: orange[700],
                "&:hover": {
                  backgroundColor: orange[900],
                },
              }}
            >
              {loading ? "Yükleniyor..." : "Listele"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Veri yoksa bilgilendirme mesajı */}
      {!loading && reportData.length === 0 && maliyetDetayData.length === 0 && (
        <Box
          sx={{
            mt: 4,
            p: 3,
            textAlign: "center",
            backgroundColor: "#fff",
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Rapor verilerini görüntülemek için yukarıdaki filtreleri ayarlayıp
            "Listele" butonuna basınız.
          </Typography>
        </Box>
      )}

      {!loading && activeTab === 0 && reportData.length > 0 && (
        <Box sx={reportSectionStyle}>
          <Typography
            variant="h6"
            align="center"
            gutterBottom
            sx={{ color: orange[800] }}
          >
            Kar/Zarar Detayları
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 5 }}>
            <Table stickyHeader size="small">
              <TableHead sx={tableHeaderStyle}>
                <TableRow>
                  <TableCell>Dönem Adı</TableCell>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Kümes Adı</TableCell>
                  <TableCell align="right">Başlangıç Hayvan</TableCell>
                  <TableCell align="right">Ölen</TableCell>
                  <TableCell align="right">Canlı Hayvan</TableCell>
                  <TableCell align="right">Kesim Günü</TableCell>
                  <TableCell align="right">Ölüm Oranı</TableCell>
                  <TableCell align="right">Ort. Ağırlık</TableCell>
                  <TableCell align="right">Toplam Et (KG)</TableCell>
                  <TableCell align="right">Toplam Yem (KG)</TableCell>
                  <TableCell align="right">YDK</TableCell>
                  <TableCell align="right">Performans Puanı</TableCell>
                  <TableCell align="right">Birim Fiyat</TableCell>
                  <TableCell align="right">Toplam Gelir</TableCell>
                  <TableCell align="right">Toplam Gider</TableCell>
                  <TableCell align="right">Net Kâr</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{item.DONEM_ADI}</TableCell>
                    <TableCell>
                      <b>{formatDate(item)}</b>
                    </TableCell>
                    <TableCell>{item.KUMES_ADI}</TableCell>
                    <TableCell align="right">
                      {Number(item.BASLANGIC_HAYVAN_SAYISI || 0).toLocaleString(
                        "tr-TR"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {Number(item.OLEN_HAYVAN_SAYISI || 0).toLocaleString(
                        "tr-TR"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {Number(item.CANLI_HAYVAN_SAYISI || 0).toLocaleString(
                        "tr-TR"
                      )}
                    </TableCell>
                    <TableCell align="right">{item.KESIM_GUNU}</TableCell>
                    <TableCell align="right">{item.OLUM_ORANI}</TableCell>
                    <TableCell align="right">{item.ORTALAMA_AGIRLIK}</TableCell>
                    <TableCell align="right">
                      {Number(item.TOPLAM_TAVUK_ETI || 0).toLocaleString(
                        "tr-TR"
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {Number(item.TOPLAM_YM_KG || 0).toLocaleString("tr-TR")}
                    </TableCell>
                    <TableCell align="right">
                      {item.YEM_DONUSUM_ORANI}
                    </TableCell>
                    <TableCell align="right">{item.PERFORMANS_PUAN}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.SATIS_BIRIM_FIYAT)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "green" }}>
                      {formatCurrency(item.TOPLAM_GELIR)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: "red" }}>
                      {formatCurrency(item.TOPLAM_GIDER)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        color: item.NET_KAR >= 0 ? "green" : "red",
                      }}
                    >
                      {formatCurrency(item.NET_KAR)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container spacing={4} direction="column">
            <Grid item>
              <Paper sx={{ p: 2, height: 400 }}>
                <Bar data={barChartData} options={barChartOptions} />
              </Paper>
            </Grid>
            <Grid item>
              <Paper sx={{ p: 2, height: 400 }}>
                <Line data={lineChartData} options={lineChartOptions} />
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {!loading && activeTab === 1 && maliyetDetayData.length > 0 && (
        <Box sx={reportSectionStyle}>
          <Typography
            variant="h6"
            align="center"
            gutterBottom
            sx={{ color: orange[800] }}
          >
            Maliyet Detayları
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 5 }}>
            <Table stickyHeader size="small">
              <TableHead sx={tableHeaderStyle}>
                <TableRow>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Kümes Adı</TableCell>
                  <TableCell>Dönem Adı</TableCell>
                  <TableCell>Maliyet Tipi</TableCell>
                  <TableCell align="right">Maliyet (TL)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {maliyetDetayData.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <b>{formatDate(item)}</b>
                    </TableCell>
                    <TableCell>{item.KUMES_ADI}</TableCell>
                    <TableCell>{item.DONEM_ADI}</TableCell>
                    <TableCell>{item.MALIYET_TIP_ADI}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      {formatCurrency(item.MALIYET_TL, 1)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography
            variant="h6"
            align="center"
            gutterBottom
            sx={{ color: orange[800] }}
          >
            Maliyet Dağılımı
          </Typography>
          {maliyetGrafikData.length > 0 ? (
            <Paper
              sx={{
                p: 2,
                height: { xs: 400, md: 500 },
                maxWidth: "800px",
                mx: "auto",
              }}
            >
              <Pie data={pieChartData} options={pieChartOptions} />
            </Paper>
          ) : (
            <Box sx={{ mt: 2, p: 3, textAlign: "center" }}>
              <Typography>Grafik için veri bulunamadı.</Typography>
            </Box>
          )}
        </Box>
      )}

      {!loading &&
        ((activeTab === 0 && reportData.length === 0) ||
          (activeTab === 1 && maliyetDetayData.length === 0)) && (
          <Box
            sx={{
              mt: 4,
              p: 3,
              textAlign: "center",
              backgroundColor: "#fff",
              borderRadius: 1,
            }}
          >
            <Typography>Görüntülenecek rapor verisi bulunamadı.</Typography>
            <Typography variant="body2" color="text.secondary">
              Lütfen bir kümes ve dönem seçip 'Listele' butonuna basınız.
            </Typography>
          </Box>
        )}
    </Paper>
  );
};

export default memo(MuhasebeRaporlari);
